<template>
    <div v-cloak>
        <app-message-list :form_errors="form_errors" :form_status="form_status" />

        <div class="form-card m-b-25">
            <div class="row">
                <div class="col-sm-12">
                    <h4 style="margin-top:0;">{{ editingId ? 'Edit Banner' : 'Create Banner' }}</h4>
                </div>

                <div class="col-sm-12 m-b-10">
                    <label>Message (use :link where the anchor should appear)</label>
                    <input class="form-control" v-model="form.message" maxlength="500" :disabled="isBusy" />
                </div>

                <div class="col-sm-8 m-b-10">
                    <label>Link URL (optional)</label>
                    <input class="form-control" v-model="form.link_url" maxlength="1000" placeholder="https://..."
                        :disabled="isBusy" />
                </div>

                <div class="col-sm-4 m-b-10">
                    <label>Link Label</label>
                    <input class="form-control" v-model="form.link_label" maxlength="100" placeholder="here"
                        :disabled="isBusy" />
                </div>

                <div class="col-sm-3 m-b-10">
                    <label>
                        <input type="checkbox" v-model="form.is_enabled" :disabled="isBusy" />
                        Enabled
                    </label>
                </div>

                <div class="col-sm-3 m-b-10">
                    <label>
                        <input type="checkbox" v-model="form.is_fixed" :disabled="isBusy" />
                        Fixed (ignore dates)
                    </label>
                </div>

                <div class="col-sm-3 m-b-10">
                    <label>Starts At</label>
                    <input class="form-control" type="datetime-local" v-model="form.starts_at"
                        :disabled="form.is_fixed || isBusy" />
                </div>

                <div class="col-sm-3 m-b-10">
                    <label>Ends At</label>
                    <input class="form-control" type="datetime-local" v-model="form.ends_at"
                        :disabled="form.is_fixed || isBusy" />
                </div>

                <div class="col-sm-12 text-right">
                    <button class="btn btn-default" @click="resetForm" :disabled="is_saving || isBusy">Clear</button>
                    <button class="btn btn-primary" @click="save" :disabled="is_saving || isBusy">
                        <span v-if="is_saving">Saving...</span>
                        <span v-else>{{ editingId ? 'Update' : 'Create' }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="raised low p-25 text-center" v-if="is_loading">
            Loading...
        </div>

        <div class="panel list-group" v-else>
            <div class="list-group-item" v-if="banners.length === 0">
                No banners yet.
            </div>

            <div class="list-group-item" v-for="(b, index) in banners" :key="b.id"
                style="display:flex; gap:10px; align-items:center;">
                <div style="flex:1;">
                    <div class="text-bold">
                        #{{ index + 1 }}

                        <span v-if="!b.is_enabled" class="label label-default m-l-5">Disabled</span>
                        <span v-if="b.is_fixed" class="label label-info m-l-5">Fixed</span>

                        <span v-if="b.is_enabled" :class="getTimeStatusClass(b)">
                            {{ getTimeStatusLabel(b) }}
                        </span>
                    </div>


                    <div>{{ b.message }}</div>
                    <div v-if="b.link_url" class="text-muted" style="font-size:12px;">
                        Link: {{ b.link_label || 'here' }} → {{ b.link_url }}
                    </div>
                </div>

                <div style="display:flex; flex-direction:column; gap:6px;">
                    <button class="btn btn-sm btn-default" @click="moveUp(b)"
                        :disabled="isBusy || isFirst(b)">↑</button>
                    <button class="btn btn-sm btn-default" @click="moveDown(b)"
                        :disabled="isBusy || isLast(b)">↓</button>
                </div>

                <div class="text-right" style="min-width:180px;">
                    <button class="btn btn-sm btn-primary" @click="edit(b)" :disabled="isBusy">Edit</button>
                    <button class="btn btn-sm btn-danger" @click="remove(b)" :disabled="isBusy">Delete</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'dashboard-banners-admin',

    data() {
        return {
            banners: [],
            is_loading: false,
            is_saving: false,
            is_reordering: false,
            editingId: null,

            form: {
                message: '',
                link_url: '',
                link_label: 'here',
                is_enabled: true,
                is_fixed: false,
                starts_at: '',
                ends_at: '',
            },

            form_errors: [],
            form_status: '',
            messageTimer: null,
        };
    },

    computed: {
        isBusy() {
            return this.is_loading || this.is_saving || this.is_reordering;
        },
    },

    methods: {
        isFirst(b) {
            return this.banners.length > 0 && this.banners[0].id === b.id;
        },

        isLast(b) {
            return this.banners.length > 0 && this.banners[this.banners.length - 1].id === b.id;
        },

        parseUtc(val) {
            if (!val) return null;

            if (/[zZ]|[+-]\d{2}:\d{2}$/.test(val)) return new Date(val);

            if (val.includes(' ')) return new Date(val.replace(' ', 'T') + 'Z');

            return new Date(val + 'Z');
        },

        getTimeStatus(b) {
            const now = new Date();

            if (b.is_fixed) return 'current';

            const start = this.parseUtc(b.starts_at);
            const end = this.parseUtc(b.ends_at);

            if (end && now > end) return 'past';

            if (start && now < start) return 'future';

            return 'current';
        },

        getTimeStatusLabel(b) {
            const s = this.getTimeStatus(b);
            if (s === 'past') return 'Past';
            if (s === 'future') return 'Future';
            return 'Current';
        },

        getTimeStatusClass(b) {
            const s = this.getTimeStatus(b);
            if (s === 'past') return 'label label-default m-l-5';
            if (s === 'future') return 'label label-warning m-l-5';
            return 'label label-success m-l-5';
        },
        loadInitialFromDom() {
            const el = document.getElementById('myLab-dashboard-banners-initial');
            if (!el) return false;
            try {
                const json = (el.textContent || el.innerText || '[]').trim();
                const parsed = JSON.parse(json || '[]');
                if (Array.isArray(parsed)) {
                    this.banners = parsed;
                    return true;
                }
            } catch (e) { }
            return false;
        },

        fetch() {
            if (this.is_loading) return;
            this.is_loading = true;

            this.$http.get('/admin/dashboard-banners/list')
                .then((res) => { this.banners = Array.isArray(res.data) ? res.data : []; })
                .catch(() => { this.banners = []; })
                .finally(() => { this.is_loading = false; });
        },

        resetForm(force = false) {
            if (!force && this.isBusy) return;

            this.editingId = null;
            this.form_errors = [];
            this.form_status = '';
            this.form = {
                message: '',
                link_url: '',
                link_label: 'here',
                is_enabled: true,
                is_fixed: false,
                starts_at: '',
                ends_at: '',
            };
        },

        toLocalDatetimeInput(val) {
            const d = this.parseUtc(val);
            if (!d || isNaN(d.getTime())) return '';

            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },

        toIsoOrNull(localDatetime) {
            if (!localDatetime) return null;
            return new Date(localDatetime).toISOString();
        },

        edit(b) {
            if (this.isBusy) return;
            this.editingId = b.id;
            this.form_errors = [];
            this.form_status = '';



            this.form = {
                message: b.message || '',
                link_url: b.link_url || '',
                link_label: b.link_label || 'here',
                is_enabled: !!b.is_enabled,
                is_fixed: !!b.is_fixed,
                starts_at: this.toLocalDatetimeInput(b.starts_at),
                ends_at: this.toLocalDatetimeInput(b.ends_at),
            };
        },

        validateForm() {
            const errors = [];

            if (!this.form.message || !this.form.message.trim()) {
                errors.push('Message is required.');
            }

            const hasStart = !!this.form.starts_at;
            const hasEnd = !!this.form.ends_at;

            if (!this.form.is_fixed) {
                if (!hasStart && !hasEnd) {
                    errors.push('Starts At and Ends At are required unless Fixed is checked.');
                }

                if (hasStart !== hasEnd) {
                    errors.push('Starts At and Ends At must both be provided.');
                }
            }

            if (!this.form.is_fixed && hasStart && hasEnd) {
                const start = new Date(this.form.starts_at);
                const end = new Date(this.form.ends_at);
                if (start > end) errors.push('Starts At must be before or equal to Ends At.');
            }

            if (this.form.link_url && !/^https?:\/\//i.test(this.form.link_url.trim())) {
                errors.push('Link URL must start with http:// or https://');
            }

            return errors;
        },

        save() {
            if (this.isBusy) return;

            const validationErrors = this.validateForm();
            if (validationErrors.length) {
                this.form_errors = validationErrors;
                this.form_status = '';
                this.scheduleAutoHide();
                return;
            }

            this.is_saving = true;
            this.form_errors = [];
            this.form_status = '';

            const payload = {
                ...this.form,
                link_url: (this.form.link_url && this.form.link_url.trim())
                    ? this.form.link_url.trim()
                    : null,
                starts_at: this.form.is_fixed ? null : this.toIsoOrNull(this.form.starts_at),
                ends_at: this.form.is_fixed ? null : this.toIsoOrNull(this.form.ends_at),
            };

            const req = this.editingId
                ? this.$http.put(`/admin/dashboard-banners/${this.editingId}`, payload)
                : this.$http.post('/admin/dashboard-banners', payload);

            req
                .then(() => {
                    this.form_status = this.editingId ? 'Banner updated.' : 'Banner created.';
                    this.form_errors = [];
                    this.scheduleAutoHide();
                    this.resetForm(true);
                    return this.fetch();
                })
                .catch((err) => {
                    this.form_status = '';
                    this.form_errors = this.extractErrors(err);
                    this.scheduleAutoHide();
                })
                .finally(() => {
                    this.is_saving = false;
                });
        },

        remove(b) {
            if (this.isBusy) return;

            const preview = (b.message || '').slice(0, 80);
            const isActiveNow = b.is_enabled && this.getTimeStatus(b) === 'current';

            const warningLine = isActiveNow
                ? '\n\n⚠ This banner is currently ACTIVE on the dashboard.'
                : '';

            swal({
                title: "Are you sure you want to delete this banner?",
                text: `"${preview}${b.message.length > 80 ? '…' : ''}"${warningLine}`,
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: "Delete",
                closeOnConfirm: true
            }, () => {
                this.deleteBanner(b);
            });
        },

        deleteBanner(b) {
            if (this.isBusy) return;

            this.is_saving = true;

            this.$http.delete(`/admin/dashboard-banners/${b.id}`)
                .then(() => {
                    this.form_status = 'Banner deleted.';
                    this.fetch();
                })
                .catch((err) => {
                    this.form_errors = this.extractErrors(err);
                })
                .finally(() => {
                    this.is_saving = false;
                });
        },

        moveUp(b) {
            if (this.isBusy) return;
            const idx = this.banners.findIndex((x) => x.id === b.id);
            if (idx <= 0) return;
            const ids = this.banners.map((x) => x.id);
            ids.splice(idx - 1, 0, ids.splice(idx, 1)[0]);
            this.saveOrder(ids);
        },

        moveDown(b) {
            if (this.isBusy) return;
            const idx = this.banners.findIndex((x) => x.id === b.id);
            if (idx === -1 || idx >= this.banners.length - 1) return;
            const ids = this.banners.map((x) => x.id);
            ids.splice(idx + 1, 0, ids.splice(idx, 1)[0]);
            this.saveOrder(ids);
        },

        saveOrder(ids) {
            if (this.isBusy) return;

            this.is_reordering = true;
            this.$http
                .patch('/admin/dashboard-banners/reorder', { ids })
                .then(() => this.fetch())
                .catch((err) => (this.form_errors = this.extractErrors(err)))
                .finally(() => {
                    this.is_reordering = false;
                });
        },

        normalizeUrl(url) {
            const u = (url || '').trim();
            if (!u) return null;
            if (/^https?:\/\//i.test(u)) return u;
            return `https://${u}`;
        },

        extractErrors(err) {
            const axiosRes = err && err.response ? err.response : null;
            const axiosData = axiosRes ? axiosRes.data : null;

            const vrData =
                (err && err.body) ||
                (err && err.data) ||
                null;

            const data = axiosData || vrData;

            if (!data) {
                const status = (axiosRes && axiosRes.status) || (err && err.status);
                return [status ? `Request failed (HTTP ${status}).` : 'Request failed.'];
            }

            if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
                const out = [];
                Object.keys(data.errors).forEach((k) => {
                    const v = data.errors[k];
                    if (Array.isArray(v)) out.push(...v);
                    else if (typeof v === 'string') out.push(v);
                });
                if (out.length) return out;
            }

            if (Array.isArray(data.errors)) return data.errors;

            if (typeof data.message === 'string') return [data.message];

            if (typeof data === 'string') return [data];

            return ['Request failed.'];
        },

        clearMessages() {
            this.form_errors = [];
            this.form_status = '';
            if (this.messageTimer) {
                clearTimeout(this.messageTimer);
                this.messageTimer = null;
            }
        },

        scheduleAutoHide() {
            if (this.messageTimer) clearTimeout(this.messageTimer);

            if ((this.form_errors && this.form_errors.length) || this.form_status) {
                this.messageTimer = setTimeout(() => {
                    this.clearMessages();
                }, 10000);
            }
        },
    },

    watch: {
        'form.is_fixed'(val) {
            if (val) {
                this.form.starts_at = '';
                this.form.ends_at = '';
            }
        },
        'form.message'() { this.clearMessages(); },
        'form.link_url'() { this.clearMessages(); },
        'form.link_label'() { this.clearMessages(); },
        'form.starts_at'() { this.clearMessages(); },
        'form.ends_at'() { this.clearMessages(); },
        'form.is_fixed'() { this.clearMessages(); },
        'form.is_enabled'() { this.clearMessages(); },
    },

    mounted() {
        const hadInitial = this.loadInitialFromDom();
        if (!hadInitial) this.fetch();
    },
};
</script>

<style scoped lang="scss">
.list-group-item {
    border-left: 0;
    border-right: 0;
}
</style>



// WEBPACK FOOTER //
// dashboard-banners-admin.vue?5030d4dd